import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import PublicLayout from "@/layouts/PublicLayout";
import { Rocket, Users, ArrowLeft, Loader2, Building2 } from "lucide-react";
import Seo from "@/components/Seo";
import CompanyLogo from "@/components/CompanyLogo";
import { useLang } from "@/context/LangContext";

/** Sprint 92al — Public project detail page. */
export default function ProjectDetailPage() {
  const { t } = useLang();
  const { slug } = useParams();
  const [p, setP] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/projects/${slug}`)
      .then((r) => setP(r.data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <PublicLayout><div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-wine-500" /></div></PublicLayout>;
  if (notFound) return (
    <PublicLayout>
      <div className="mx-auto max-w-xl py-24 text-center">
        <Rocket className="mx-auto h-12 w-12 text-neutral-300" />
        <h1 className="mt-4 text-2xl font-bold text-navy" data-testid="project-not-found">{t("proj_not_found")}</h1>
        <Link to="/projects" className="mt-6 inline-flex items-center gap-1 text-sm font-bold text-wine-700"><ArrowLeft className="h-3 w-3" /> {t("proj_back")}</Link>
      </div>
    </PublicLayout>
  );

  return (
    <PublicLayout>
      <Seo title={`${p.title} — ${t("proj_tag_full")}`} description={p.subtitle || p.description?.slice(0, 180)} image={p.image || undefined} />
      <section className="relative overflow-hidden bg-gradient-to-br from-wine-900 via-wine-800 to-pink-700 py-16 text-white">
        {p.image && <img src={p.image} alt="" className="absolute inset-0 h-full w-full object-cover opacity-20" />}
        <div className="relative mx-auto max-w-4xl px-4">
          <Link to="/projects" className="inline-flex items-center gap-1 text-xs font-semibold text-white/70 hover:text-white"><ArrowLeft className="h-3 w-3" /> {t("proj_all")}</Link>
          <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1 text-xs font-bold uppercase tracking-wider backdrop-blur">
            <Rocket className="h-3 w-3" /> {t("proj_tag_full")}
          </div>
          <h1 className="mt-4 text-4xl font-black leading-tight sm:text-5xl">{p.title}</h1>
          {p.subtitle && <p className="mt-3 text-xl text-pink-100">{p.subtitle}</p>}
          <div className="mt-6 inline-flex items-center gap-1.5 rounded-full bg-white/15 px-4 py-1.5 text-sm font-bold backdrop-blur">
            <Users className="h-4 w-4" /> {t(p.member_count > 1 ? "proj_members_many" : "proj_members_one").replace("{n}", p.member_count)}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-4xl px-4 py-14">
        {p.description && (
          <div className="prose max-w-none whitespace-pre-wrap text-neutral-700" data-testid="project-description">{p.description}</div>
        )}

        <h2 className="mt-14 text-2xl font-bold text-navy">{t("proj_members_title")}</h2>
        {(p.members || []).length === 0 ? (
          <p className="mt-4 text-sm italic text-neutral-500">{t("proj_members_empty")}</p>
        ) : (
          <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="project-members-grid">
            {p.members.map((m) => (
              <Link key={m.id} to={`/directory/${m.slug}`} className="group flex items-center gap-3 rounded-2xl border border-neutral-100 bg-white p-4 shadow-sm hover:border-wine-200 hover:shadow-md" data-testid={`project-member-${m.slug}`}>
                <CompanyLogo src={m.logo} name={m.name} size={48} className="rounded-xl" />
                <div className="min-w-0 flex-1">
                  <div className="truncate font-bold text-navy group-hover:text-wine-700">{m.name}</div>
                  <div className="truncate text-xs text-neutral-500"><Building2 className="inline h-3 w-3" /> {m.country || "—"}</div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>
    </PublicLayout>
  );
}
